import React from "react";
import { MapPin } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative w-full pt-28 md:pt-32 pb-24"
    >
      <div className="relative max-w-3xl mx-auto px-6 text-center fade-up">
        {/* Top ornament */}
        <div className="flex justify-center mb-6" aria-hidden>
          <span className="block w-px h-12 bg-gradient-to-b from-transparent via-[#C9A961] to-[#C9A961]" />
        </div>
        <p className="gold-rule mb-6 bismillah-rule" data-testid="hero-tagline">
          <span
            dir="rtl"
            className="font-arabic block whitespace-nowrap bismillah-text"
          >
            بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيمِ
          </span>
        </p>

        {/* Arabic verse — Sourate Ar-Rum 30:21 */}
        <p
          dir="rtl"
          className="font-arabic text-emerald-deep tracking-normal"
          style={{
            lineHeight: 2.6,
            fontSize: "clamp(15px, 4.2vw, 30px)",
          }}
        >
          <span className="block whitespace-nowrap">
            وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُمْ مِنْ أَنْفُسِكُمْ أَزْوَاجًا
          </span>
          <span className="block whitespace-nowrap mt-3">
            لِتَسْكُنُوا إِلَيْهَا وَجَعَلَ بَيْنَكُمْ مَوَدَّةً وَرَحْمَةً
          </span>
        </p>

        {/* French translation */}
        <p className="font-serif-display italic text-emerald text-lg md:text-2xl mt-6 max-w-2xl mx-auto leading-relaxed">
          « Et parmi Ses signes, Il a créé de vous, pour vous, des épouses
          afin que vous trouviez auprès d’elles tranquillité, et Il a mis
          entre vous affection et bonté. »
        </p>
        <p className="text-gold tracking-[0.4em] text-[11px] md:text-xs mt-3">
          ✦ SOURATE AR-RUM ( 30 : 21 ) ✦
        </p>

        <div className="divider-ornament my-10">
          <span className="line" />
          <span className="dot" />
          <span className="line" />
        </div>

        {/* Families */}
        <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-6 items-center max-w-2xl mx-auto">
          <div className="text-center sm:text-right">
            <p className="text-gold text-[11px] tracking-[0.45em]">LA FAMILLE</p>
            <h3 className="font-serif-display text-emerald-deep text-3xl md:text-4xl font-semibold mt-1">
              Alhadji Amane
            </h3>
          </div>
          <span className="font-script gold-text text-5xl md:text-6xl px-2">&amp;</span>
          <div className="text-center sm:text-left">
            <p className="text-gold text-[11px] tracking-[0.45em]">LA FAMILLE</p>
            <h3 className="font-serif-display text-emerald-deep text-3xl md:text-4xl font-semibold mt-1">
              Alhadji Oumar
            </h3>
          </div>
        </div>

        <p className="font-serif-display italic text-emerald-deep/80 text-lg md:text-xl mt-8 max-w-xl mx-auto">
          … ont la joie de vous inviter à célébrer le mariage de leurs enfants
        </p>

        {/* Couple names — the headline */}
        <div className="mt-10 mb-4">
          <h1
            className="font-script leading-[0.95]"
            style={{ color: "#063524" }}
          >
            <span className="block text-6xl sm:text-7xl md:text-8xl text-center drop-shadow-[0_2px_2px_rgba(6,53,36,0.15)]">
              Yero Oumar
            </span>
            <span className="block shimmer-text my-3 text-4xl sm:text-5xl">&amp;</span>
            <span className="block text-6xl sm:text-7xl md:text-8xl text-center drop-shadow-[0_2px_2px_rgba(6,53,36,0.15)]">
              Maïmouna M. Toukour
            </span>
          </h1>
        </div>

        {/* Date banner — emerald with gold trim, replicating the invitation */}
        <div className="mt-12 inline-block w-full max-w-2xl">
          <div
            className="relative px-8 py-6 mx-auto"
            style={{
              background:
                "linear-gradient(180deg, #145C42 0%, #0a3d2e 100%)",
              border: "2px solid #C9A961",
              boxShadow:
                "inset 0 0 0 4px rgba(10,61,46,1), inset 0 0 0 5px #C9A961, 0 20px 50px -20px rgba(10,61,46,0.6)",
            }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8">
              <div className="text-center">
                <p
                  className="font-serif-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-wider"
                  style={{ color: "#9D6E39" }}
                >
                  12 — 15 AOÛT 2026
                </p>
              </div>
              <span
                aria-hidden
                className="h-8 w-px hidden sm:block"
                style={{ background: "#C9A961" }}
              />
              <div className="flex items-center gap-2" style={{ color: "#9D6E39" }}>
                <MapPin size={20} />
                <p className="font-serif-display text-xl md:text-2xl tracking-[0.18em]">
                  BITAM, GABON
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            type="button"
            data-testid="hero-rsvp-btn"
            onClick={() => scrollTo("contact")}
            className="btn-gold"
          >
            Confirmer ma présence
          </button>
          <button
            type="button"
            data-testid="hero-programme-btn"
            onClick={() => scrollTo("programme")}
            className="btn-outline-gold"
          >
            Voir le programme
          </button>
        </div>

        {/* Scroll cue */}
        <div className="mt-14 text-gold text-[11px] tracking-[0.45em]">↓ DÉFILEZ</div>
      </div>
    </section>
  );
}
